import { LitElement, css, html } from 'lit'
import type { TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import '@github/clipboard-copy-element'
import { isNullishOrWhitespace } from '../../functions/nullish';

type UserClassification = 'internal' | 'external' | 'goodcontact' | 'organization';

@customElement('ep-timeline-user')
export class EPTimelineUser extends LitElement {

  @property({ reflect: true })
  name: string = "";

  @property({ attribute: 'acting-role', reflect: true })
  actingRole?: string;

  @property({ reflect: true })
  department?: string;

  @property({ reflect: true })
  email?: string;

  @property({ reflect: true })
  phone?: string;

  @property({  reflect: true })
  type?: UserClassification;

  private renderRoleDepartment() {
    if (!isNullishOrWhitespace(this.actingRole) && !isNullishOrWhitespace(this.department)) {
      return html`
        <span><strong>${this.actingRole}</strong> for <strong>${this.department}</strong></span>
      `
    }
    else if (!isNullishOrWhitespace(this.actingRole)) {
      return html`
        <strong>${this.actingRole}</strong>
      `
    }
    else if (!isNullishOrWhitespace(this.department)) {
      return html`
        <span>Representing: <strong>${this.department}</strong></span>
      `
    }
    return html``
  }

  private renderCopyableText(value: string, displayText?: string) {
    const disp = displayText ?? value;

    const handleCopy = (event: CustomEvent) => {
      if (event.target instanceof HTMLElement) {
        const parent = event.target.closest('.clipboardable');
        if (parent instanceof HTMLElement && !parent.classList.contains('copied')) {
          parent.classList.add('copied');
          setTimeout(() => {
            parent.classList.remove('copied')
          }, 1000);
          //console.log('copy?', parent);
        }        
      }
    }
    
    return html`
      <span class="clipboardable">
        <clipboard-copy value=${value} @clipboard-copy=${handleCopy}>
          ${disp}
        </clipboard-copy>
      </span>
    `
  }

  private renderContactInfo() {
    const classification: Record<Lowercase<UserClassification>, TemplateResult> = {
      internal: html`<span>🏛️ Internal User</span>`,
      external: html`<span>🌎 External User</span>`,
      goodcontact: html`<span>📇 Good Contact</span>`,
      organization: html`<span>🏢 Organization</span>`,
    }

    return html`
      <div class="contact-info">
        ${ isNullishOrWhitespace(this.email) ? '' : html`<span>📧 ${this.renderCopyableText(this.email)}</span>` }
        ${ isNullishOrWhitespace(this.phone) ? '' : html`<span>📞 ${this.renderCopyableText(this.phone)}</span>` }
        ${
          this.type !== undefined
          ? classification[this.type.toLowerCase() as keyof Record<UserClassification, any>]
          : ''
        }
      </div>
    `
  }

  private renderTooltip() {
    return html`
      <div class="tooltip">
        <strong style="font-size: 1.15em;">${this.name}</strong>
        <div class="deptRole">
          ${this.renderRoleDepartment()}
        </div>
        ${this.renderContactInfo()}
      </div>
    `
  }

  render() {
    return html`
      <overlay-trigger placement="top">
        <strong part="trigger" slot="trigger"><slot></slot></strong>
        <ep-tooltip slot="hover-content" color="light" placement="top">
          ${this.renderTooltip()}
        </ep-tooltip>
        <ep-tooltip slot="click-content" color="light" placement="top">
          ${this.renderTooltip()}
        </ep-tooltip>
      </overlay-trigger>
    `
  } 

  // <slot name="tooltip" @slotchange=${this._handleTooltipSlotChange}></slot>

  static styles = css`
    :host {
      display: inline;
      --text-default: rgb(31, 35, 40);
      --text-muted: rgb(101, 109, 118);
      --border-muted: rgb(216, 222, 228);
      --border-default: rgb(208, 215, 222);
      --badge-background: rgb(234, 238, 242);
      --canvas-subtle: rgb(246, 248, 250);
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
      font-family: var(--font-prose);
      color: var(--text-default);
    }

    .tooltip {
      display: flex;
      flex-direction: column;
      color: var(--text-default);
    }

    strong {
      font-weight: 600;
    }

    .deptRole, .contact-info {
      color: var(--text-muted);
    }

    .contact-info {
      display: flex;
      flex-direction: column;
    }

    [part=trigger]:hover, .clipboardable:hover {
      text-decoration: 1px dashed underline;
      text-underline-offset: 2px;
      cursor: pointer;
    }

    @keyframes rise {
      from {
        transform: translateY(-25%);
        opacity: 1.0;
      }
      to {
        transform: translateY(-125%);
        opacity: 0.0;
      }
    }
    .clipboardable.copied::after {
      content: 'Copied ✔';
      color: green;
      position: absolute;
      right: calc(50% - 57px/2);
      user-select: none;
      animation: 1000ms rise;
    }
    .clipboardable:active {
      color: green;
    }
    .clipboardable clipboard-copy:focus-visible {
      outline: none;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-timeline-user': EPTimelineUser
  }
}
